import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { contractIntakeAPI, propertyAPI, propertyUnitAPI } from '../../services/api';
import { apiErrorMessage } from '../../utils/apiError';
import Modal from '../Common/admin/Modal';

const SHOW_SAMPLE = process.env.NODE_ENV !== 'production';

const FREQUENCIES = ['monthly', 'quarterly', 'semiAnnual', 'single'];

const EMPTY_FORM = {
  dateFrom: '',
  dateTo: '',
  contractType: 0,
  tenantName: '',
  identityNumber: '',
  dob: '',
  mobileNumber: '',
  emailId: '',
  propertyId: '',
  totalRent: '',
  securityDepositAmount: '',
  paymentFrequency: 'monthly',
};

const today = () => new Date().toISOString().slice(0, 10);
const plusYear = (from) => {
  const d = new Date(from);
  d.setFullYear(d.getFullYear() + 1);
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

/**
 * "Add contract" in the same wide three-column dialog as the property intake:
 * contract dates, tenant, the property with its units, and payment terms.
 */
const ContractIntakeModal = ({
  show,
  onClose,
  onSaved,
  propertyId = null,
  contractId = null,
  mode = 'create',
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [errors, setErrors] = useState({});
  const [failure, setFailure] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const readOnly = mode === 'view';

  useEffect(() => {
    if (!show) return;
    const from = today();
    setForm({ ...EMPTY_FORM, dateFrom: from, dateTo: plusYear(from), propertyId: propertyId || '' });
    setSelectedUnits([]);
    setUnits([]);
    setErrors({});
    setFailure(null);

    propertyAPI
      .getAll('', 1, 200)
      .then((res) => setProperties(res.data.items || []))
      .catch((err) => console.error('Failed to load properties:', err));

    if (!contractId) return;

    setLoading(true);
    contractIntakeAPI
      .getById(contractId)
      .then(({ data }) => {
        setForm({
          dateFrom: data.dateFrom ? String(data.dateFrom).slice(0, 10) : '',
          dateTo: data.dateTo ? String(data.dateTo).slice(0, 10) : '',
          contractType: data.contractType ?? 0,
          tenantName: data.tenantName || '',
          identityNumber: data.identityNumber || '',
          dob: data.dob ? String(data.dob).slice(0, 10) : '',
          mobileNumber: data.mobileNumber || '',
          emailId: data.emailId || '',
          propertyId: data.propertyId || '',
          totalRent: data.totalRent ?? '',
          securityDepositAmount: data.securityDepositAmount ?? '',
          paymentFrequency: data.paymentFrequency || 'monthly',
        });
        setSelectedUnits(data.unitIds || []);
      })
      .catch((err) => setFailure(apiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [show, propertyId, contractId]);

  // Units follow the chosen property.
  useEffect(() => {
    if (!show || !form.propertyId) {
      setUnits([]);
      return;
    }
    propertyUnitAPI
      .getByProperty(form.propertyId)
      .then(({ data }) => setUnits(data || []))
      .catch((err) => console.error('Failed to load units:', err));
  }, [show, form.propertyId]);

  const set = (name) => (e) => {
    const { value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      // Keep the one-year default in step with the start date.
      if (name === 'dateFrom' && value) next.dateTo = plusYear(value);
      return next;
    });
    if (name === 'propertyId') setSelectedUnits([]);
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const toggleUnit = (id) =>
    setSelectedUnits((prev) =>
      prev.includes(id) ? prev.filter((u) => u !== id) : [...prev, id]
    );

  // Units load asynchronously after the property is set, so remember that the
  // sample still owes us a unit selection and apply it when they arrive.
  const [sampleWantsUnits, setSampleWantsUnits] = useState(false);

  useEffect(() => {
    if (!sampleWantsUnits || units.length === 0) return;
    setSelectedUnits(units.slice(0, 2).map((u) => u.id));
    setSampleWantsUnits(false);
  }, [sampleWantsUnits, units]);

  const fillSample = () => {
    const propertyForSample = form.propertyId || properties[0]?.id || '';
    setForm((prev) => ({
      ...prev,
      tenantName: 'سعد عبدالعزيز القحطاني',
      identityNumber: '2020202020',
      dob: '1992-09-03',
      mobileNumber: '0559876543',
      emailId: 'saad@example.com',
      totalRent: '48000',
      securityDepositAmount: '4000',
      paymentFrequency: 'quarterly',
      propertyId: propertyForSample,
    }));

    if (units.length > 0 && propertyForSample === form.propertyId) {
      setSelectedUnits(units.slice(0, 2).map((u) => u.id));
    } else {
      setSampleWantsUnits(true);
    }

    setErrors({});
    setFailure(null);
  };

  const submit = async (e) => {
    e.preventDefault();

    const found = {};
    if (!form.dateFrom) found.dateFrom = t('tree.required');
    if (!form.tenantName.trim()) found.tenantName = t('tree.required');
    if (!form.propertyId) found.propertyId = t('tree.required');
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSaving(true);
    setFailure(null);
    try {
      const payload = {
        dateFrom: form.dateFrom,
        dateTo: form.dateTo || null,
        contractType: Number(form.contractType) || 0,
        tenantName: form.tenantName,
        identityNumber: form.identityNumber || null,
        dob: form.dob || null,
        mobileNumber: form.mobileNumber || null,
        emailId: form.emailId || null,
        propertyId: Number(form.propertyId),
        unitIds: selectedUnits,
        totalRent: form.totalRent ? Number(form.totalRent) : null,
        securityDepositAmount: form.securityDepositAmount
          ? Number(form.securityDepositAmount)
          : null,
        paymentFrequency: form.paymentFrequency,
      };

      if (contractId) await contractIntakeAPI.update(contractId, payload);
      else await contractIntakeAPI.create(payload);
      onSaved();
      onClose();
    } catch (err) {
      console.error('Contract intake failed:', err);
      setFailure(apiErrorMessage(err));
      setSaving(false);
    }
  };

  const field = (name, labelKey, icon, { type = 'text', required = false } = {}) => (
    <div className="ad-modal-field">
      <label className={`ad-modal-label ${required ? 'ad-label-required' : ''}`}>
        {t(`contract.intake.${labelKey}`)}
      </label>
      <span className="ad-input-wrap">
        <i className={`fas ${icon} ad-input-icon`}></i>
        <input
          type={type}
          className={`ad-input-sm ad-input-has-icon ${errors[name] ? 'ad-input-error' : ''}`}
          placeholder={t(`contract.intake.${labelKey}`)}
          value={form[name]}
          onChange={set(name)}
          disabled={readOnly}
        />
      </span>
      {errors[name] && <span className="ad-error-text">{errors[name]}</span>}
    </div>
  );

  // The instalment preview makes the chosen schedule concrete.
  const perYear = { monthly: 12, quarterly: 4, semiAnnual: 2, single: 1 }[form.paymentFrequency] || 1;
  const instalment = form.totalRent ? Number(form.totalRent) / perYear : null;

  return (
    <Modal
      show={show}
      title={t(`contract.intake.${mode}Title`)}
      size="wide"
      onClose={onClose}
      footer={
        <>
          {SHOW_SAMPLE && !readOnly && (
            <button type="button" className="ad-btn ad-btn-ghost" onClick={fillSample} disabled={saving}>
              <i className="fas fa-wand-magic-sparkles"></i>
              {t('property.intake.fillSample')}
            </button>
          )}
          <button type="button" className="ad-btn" onClick={onClose} disabled={saving}>
            {readOnly ? t('common.close') : t('common.cancel')}
          </button>
          {!readOnly && (
            <button
              type="submit"
              form="contract-intake-form"
              className="ad-btn ad-btn-primary"
              disabled={saving || loading}
            >
              {saving ? t('common.loading') : t('common.save')}
            </button>
          )}
        </>
      }
    >
      <form id="contract-intake-form" onSubmit={submit}>
        {failure && <p className="ad-error-text">{failure}</p>}

        <div className="ad-intake-columns">
          {/* --- tenant -------------------------------------------------- */}
          <section className="ad-intake-col">
            <h3 className="ad-intake-heading">
              <i className="fas fa-user"></i>
              {t('contract.intake.tenantSection')}
            </h3>
            <div className="ad-modal-grid">
              {field('tenantName', 'tenantName', 'fa-user', { required: true })}
              {field('identityNumber', 'identityNumber', 'fa-id-card')}
              {field('dob', 'dob', 'fa-cake-candles', { type: 'date' })}
              {field('mobileNumber', 'mobileNumber', 'fa-mobile-screen')}
              {field('emailId', 'email', 'fa-envelope', { type: 'email' })}
            </div>

            <h4 className="ad-intake-subheading">{t('contract.intake.termSection')}</h4>
            <div className="ad-modal-grid">
              {field('dateFrom', 'dateFrom', 'fa-calendar-day', { type: 'date', required: true })}
              {field('dateTo', 'dateTo', 'fa-calendar-check', { type: 'date' })}
            </div>
          </section>

          {/* --- property & units ---------------------------------------- */}
          <section className="ad-intake-col">
            <h3 className="ad-intake-heading">
              <i className="fas fa-building"></i>
              {t('contract.intake.propertySection')}
            </h3>

            <div className="ad-modal-grid">
              <div className="ad-modal-field ad-modal-field-full">
                <label className="ad-modal-label ad-label-required">
                  {t('contract.intake.property')}
                </label>
                <span className="ad-input-wrap">
                  <i className="fas fa-building ad-input-icon"></i>
                  <select
                    className={`ad-input-sm ad-input-has-icon ${
                      errors.propertyId ? 'ad-input-error' : ''
                    }`}
                    value={form.propertyId}
                    onChange={set('propertyId')}
                    disabled={readOnly}
                  >
                    <option value="">{t('contract.intake.property')}</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nameAr || p.nameEng || p.code}
                      </option>
                    ))}
                  </select>
                </span>
                {errors.propertyId && (
                  <span className="ad-error-text">{errors.propertyId}</span>
                )}
              </div>
            </div>

            <h4 className="ad-intake-subheading">
              {t('contract.intake.units')}
              {selectedUnits.length > 0 && ` (${selectedUnits.length})`}
            </h4>
            <div className="ad-unit-picker">
              {!form.propertyId && <p className="ad-hint">{t('contract.intake.pickProperty')}</p>}
              {form.propertyId && units.length === 0 && (
                <p className="ad-hint">{t('tree.empty')}</p>
              )}
              {units.map((unit) => (
                <label key={unit.id} className="ad-unit-pick">
                  <input
                    type="checkbox"
                    className="ad-checkbox"
                    checked={selectedUnits.includes(unit.id)}
                    onChange={() => toggleUnit(unit.id)}
                    disabled={readOnly}
                  />
                  <span>{unit.unitNumber || `#${unit.id}`}</span>
                </label>
              ))}
            </div>
          </section>

          {/* --- payment ------------------------------------------------- */}
          <section className="ad-intake-col">
            <h3 className="ad-intake-heading">
              <i className="fas fa-money-bill-wave"></i>
              {t('contract.intake.paymentSection')}
            </h3>
            <div className="ad-modal-grid">
              {field('totalRent', 'totalRent', 'fa-sack-dollar', { type: 'number' })}
              {field('securityDepositAmount', 'deposit', 'fa-shield-halved', { type: 'number' })}

              <div className="ad-modal-field ad-modal-field-full">
                <label className="ad-modal-label">{t('contract.intake.frequency')}</label>
                <div className="ad-freq-options">
                  {FREQUENCIES.map((value) => (
                    <label
                      key={value}
                      className={`ad-freq-option ${
                        form.paymentFrequency === value ? 'ad-freq-option-active' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="paymentFrequency"
                        value={value}
                        checked={form.paymentFrequency === value}
                        onChange={set('paymentFrequency')}
                        disabled={readOnly}
                      />
                      {t(`contract.intake.frequencies.${value}`)}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {instalment !== null && (
              <p className="ad-instalment">
                {t('contract.intake.instalmentPreview', {
                  count: perYear,
                  amount: instalment.toLocaleString('en-US', { maximumFractionDigits: 2 }),
                })}
              </p>
            )}
          </section>
        </div>
      </form>
    </Modal>
  );
};

export default ContractIntakeModal;
